/**
 * Company product packages + capability catalog.
 *
 * Capability codes match `docs/RBAC_FEATURES_AND_CAPABILITIES.html` seed catalog.
 * Gates should check these codes (e.g. `insights.data_quality`) so when
 * `company_capability` is loaded from the DB later, call sites barely change:
 *   today → PROFILE_ENABLED_CAPABILITIES[product_profile]
 *   later → company.capabilities.includes(code)
 */

export const PRODUCT_PROFILE = {
  STANDARD: 'standard',
  HIGHLIGHTS: 'highlights',
  ENTERPRISE_MOBILITY: 'enterprise_mobility',
  DQH: 'dqh',
};

/**
 * Capability codes — same strings as the future DB `capability.code` column.
 * Parent menus are derived from “any child capability enabled,” not separate codes.
 */
export const CAPABILITY = {
  // administration
  ADMIN_USERS: 'admin.users',
  ADMIN_COMPANIES: 'admin.companies',
  ADMIN_COMPANY_SETTINGS: 'admin.company_settings',

  // ingestion
  INGESTION_CONNECTORS: 'ingestion.connectors',
  INGESTION_CONFIGURATION: 'ingestion.configuration',

  // orchestration
  ORCHESTRATION_HUB: 'orchestration.hub',

  // identity resolution
  IDR_MATCH_AI_CONFIG: 'idr.match_ai_config',
  IDR_MATCH_AI_LENS: 'idr.match_ai_lens',
  IDR_GRAPH: 'idr.graph',
  IDR_COOKIE_LOOKUP: 'idr.cookie_lookup',
  IDR_CLIENT_SPINE: 'idr.client_spine',
  IDR_ATTRIBUTE_WEIGHTAGE: 'idr.attribute_weightage',

  // insights
  INSIGHTS_OPERATIONAL_DASHBOARD: 'insights.operational_dashboard',
  INSIGHTS_CONSOLE: 'insights.console',
  INSIGHTS_DATA_QUALITY: 'insights.data_quality',
  INSIGHTS_RISK_ANALYSIS: 'insights.risk_analysis',
  INSIGHTS_MERGE_MANAGER: 'insights.merge_manager',
  INSIGHTS_FILE_MONITORING: 'insights.file_monitoring',

  // monitoring
  MONITORING_AIRFLOW: 'monitoring.airflow',

  // business hierarchy
  BUSINESS_HIERARCHY_MATCH_OUTPUT: 'business_hierarchy.match_output_records',

  // data delivery
  DELIVERY_META_STORE: 'delivery.meta_store',
  DELIVERY_DATA_EXTRACTS: 'delivery.data_extracts',
  DELIVERY_OUTBOUND_CONNECTORS: 'delivery.outbound_connectors',
  DELIVERY_APIS: 'delivery.apis',
  DELIVERY_LOOKUP: 'delivery.lookup',

  // standardized fields
  STD_FIELDS_FIELDS_RULES: 'std_fields.fields_rules',

  // dqh
  DQH_HOME: 'dqh.home',
  DQH_CONNECTORS_INBOUND: 'dqh.connectors_inbound',
  DQH_CONNECTORS_OUTBOUND: 'dqh.connectors_outbound',

  // knowledge graph
  KG_INTELLIGENCE_CONSOLE: 'kg.intelligence_console',
  KG_MY_AUDIENCES: 'kg.my_audiences',
  KG_DASHBOARDS: 'kg.dashboards',
  KG_SETTINGS: 'kg.settings',

  // reporting
  REPORTING_HOME: 'reporting.home',

  /**
   * Not in the RBAC seed table yet (Ask Da is header chrome, not a nav capability).
   * Keep a stable code so we can seed it later without renaming gates.
   */
  PLATFORM_ASK_DA: 'platform.ask_da',
};

export const ALL_CAPABILITIES = Object.values(CAPABILITY);

/** Insights children — parent Insights menu shows if any of these are enabled. */
export const INSIGHTS_CAPABILITIES = [
  CAPABILITY.INSIGHTS_OPERATIONAL_DASHBOARD,
  CAPABILITY.INSIGHTS_CONSOLE,
  CAPABILITY.INSIGHTS_DATA_QUALITY,
  CAPABILITY.INSIGHTS_RISK_ANALYSIS,
  CAPABILITY.INSIGHTS_MERGE_MANAGER,
  CAPABILITY.INSIGHTS_FILE_MONITORING,
];

export const INGESTION_CAPABILITIES = [
  CAPABILITY.INGESTION_CONNECTORS,
  CAPABILITY.INGESTION_CONFIGURATION,
];

export const IDR_CAPABILITIES = [
  CAPABILITY.IDR_MATCH_AI_CONFIG,
  CAPABILITY.IDR_MATCH_AI_LENS,
  CAPABILITY.IDR_GRAPH,
  CAPABILITY.IDR_COOKIE_LOOKUP,
  CAPABILITY.IDR_CLIENT_SPINE,
  CAPABILITY.IDR_ATTRIBUTE_WEIGHTAGE,
];

export const DELIVERY_CAPABILITIES = [
  CAPABILITY.DELIVERY_META_STORE,
  CAPABILITY.DELIVERY_DATA_EXTRACTS,
  CAPABILITY.DELIVERY_OUTBOUND_CONNECTORS,
  CAPABILITY.DELIVERY_APIS,
  CAPABILITY.DELIVERY_LOOKUP,
];

export const KG_CAPABILITIES = [
  CAPABILITY.KG_INTELLIGENCE_CONSOLE,
  CAPABILITY.KG_MY_AUDIENCES,
  CAPABILITY.KG_DASHBOARDS,
  CAPABILITY.KG_SETTINGS,
];

export const ADMIN_CAPABILITIES = [
  CAPABILITY.ADMIN_USERS,
  CAPABILITY.ADMIN_COMPANIES,
  CAPABILITY.ADMIN_COMPANY_SETTINGS,
];

export const DQH_CAPABILITIES = [
  CAPABILITY.DQH_HOME,
  CAPABILITY.DQH_CONNECTORS_INBOUND,
  CAPABILITY.DQH_CONNECTORS_OUTBOUND,
];

/**
 * Allow-list per product_profile.
 * Later: replace with company_capability rows from the API; keep the same codes.
 *
 * STANDARD excludes DQH-only codes so those nav items appear only on the dqh package.
 */
export const PROFILE_ENABLED_CAPABILITIES = {
  [PRODUCT_PROFILE.STANDARD]: ALL_CAPABILITIES.filter(
    code => !DQH_CAPABILITIES.includes(code)
  ),

  [PRODUCT_PROFILE.HIGHLIGHTS]: [
    CAPABILITY.INGESTION_CONNECTORS,
    CAPABILITY.INGESTION_CONFIGURATION,
    CAPABILITY.INSIGHTS_OPERATIONAL_DASHBOARD,
    CAPABILITY.INSIGHTS_DATA_QUALITY,
    CAPABILITY.INSIGHTS_CONSOLE,
    CAPABILITY.DELIVERY_LOOKUP,
    CAPABILITY.ADMIN_USERS,
    CAPABILITY.ADMIN_COMPANY_SETTINGS,
    CAPABILITY.IDR_ATTRIBUTE_WEIGHTAGE,
    CAPABILITY.MONITORING_AIRFLOW,
  ],

  [PRODUCT_PROFILE.ENTERPRISE_MOBILITY]: [
    CAPABILITY.INGESTION_CONNECTORS,
    CAPABILITY.INSIGHTS_OPERATIONAL_DASHBOARD,
    CAPABILITY.MONITORING_AIRFLOW,
    CAPABILITY.STD_FIELDS_FIELDS_RULES,
  ],

  [PRODUCT_PROFILE.DQH]: [...DQH_CAPABILITIES],
};

/**
 * Lookup tab visibility per product_profile.
 * Defaults: both Business and Individual tabs visible.
 */
export const LOOKUP_UI_DEFAULTS = {
  hideBusinessTab: false,
  hideIndividualTab: false,
};

export const PROFILE_LOOKUP_UI = {
  [PRODUCT_PROFILE.STANDARD]: { ...LOOKUP_UI_DEFAULTS },
  // Business lookup only
  [PRODUCT_PROFILE.HIGHLIGHTS]: {
    hideBusinessTab: true,
    hideIndividualTab: false,
  },
  // Individual lookup only
  [PRODUCT_PROFILE.ENTERPRISE_MOBILITY]: {
    hideBusinessTab: false,
    hideIndividualTab: true,
  },
  [PRODUCT_PROFILE.DQH]: { ...LOOKUP_UI_DEFAULTS },
};

/**
 * Ingestion Step 1 UI per product_profile.
 * Dropdowns still list every option; non-allowed ones render disabled.
 * null dataCategories / volumeOptionPrefix = all options selectable.
 */
export const INGESTION_UI_DEFAULTS = {
  /** Selectable Data Category labels; null = all categories selectable. */
  dataCategories: null,
  showPriorityProcessing: true,
  /** Selectable volume options whose label starts with this; null = all. */
  volumeOptionPrefix: null,
};

export const PROFILE_INGESTION_UI = {
  [PRODUCT_PROFILE.STANDARD]: { ...INGESTION_UI_DEFAULTS },
  [PRODUCT_PROFILE.HIGHLIGHTS]: {
    dataCategories: ['Customer Data'],
    showPriorityProcessing: false,
    volumeOptionPrefix: 'Weekly',
  },
  [PRODUCT_PROFILE.ENTERPRISE_MOBILITY]: { ...INGESTION_UI_DEFAULTS },
  [PRODUCT_PROFILE.DQH]: { ...INGESTION_UI_DEFAULTS },
};

/**
 * Connector source visibility per product_profile.
 * null visibleConnectors = show all; otherwise allow-list of source ids per type.
 */
export const CONNECTOR_UI_DEFAULTS = {
  /** Allow-list for Sources tiles; null = show all. */
  visibleConnectors: null,
  /** Allow-list for Configured Connector Details; null = falls back to visibleConnectors. */
  configuredConnectors: null,
  /** Shown as disabled, non-clickable source tiles; null = none. */
  readOnlyConnectors: null,
};

export const PROFILE_CONNECTOR_UI = {
  [PRODUCT_PROFILE.STANDARD]: { ...CONNECTOR_UI_DEFAULTS },
  [PRODUCT_PROFILE.HIGHLIGHTS]: {
    visibleConnectors: {
      INBOUND: [],
      OUTBOUND: [],
    },
    configuredConnectors: {
      INBOUND: ['MFT'],
      OUTBOUND: [],
    },
    readOnlyConnectors: {
      INBOUND: ['S3', 'AZURE_BLOB', 'MFT'],
      OUTBOUND: [],
    },
  },
  [PRODUCT_PROFILE.ENTERPRISE_MOBILITY]: { ...CONNECTOR_UI_DEFAULTS },
  [PRODUCT_PROFILE.DQH]: { ...CONNECTOR_UI_DEFAULTS },
};

/**
 * Attribute Weightage UI per product_profile.
 */
export const ATTRIBUTE_WEIGHTAGE_UI_DEFAULTS = {
  /** When false, Edit Weightage is shown disabled (view-only). */
  allowEditWeightage: true,
};

export const PROFILE_ATTRIBUTE_WEIGHTAGE_UI = {
  [PRODUCT_PROFILE.STANDARD]: { ...ATTRIBUTE_WEIGHTAGE_UI_DEFAULTS },
  [PRODUCT_PROFILE.HIGHLIGHTS]: {
    allowEditWeightage: false,
  },
  [PRODUCT_PROFILE.ENTERPRISE_MOBILITY]: { ...ATTRIBUTE_WEIGHTAGE_UI_DEFAULTS },
  [PRODUCT_PROFILE.DQH]: { ...ATTRIBUTE_WEIGHTAGE_UI_DEFAULTS },
};
